import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Send, Bug, Lightbulb, Wand2, Plus, X, ShieldCheck, BarChart3, Power, Flag, Check, Trash2, Bot, Sparkles, RefreshCw, AlertTriangle, Rocket, MessageSquare, Cpu, Euro, Gauge, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
    feedbackAdminThreads, feedbackGetThread, feedbackReply, feedbackScreenshot,
    feedbackAdminUpdateStatus, feedbackSurveys, feedbackCreateSurvey, feedbackToggleSurvey, feedbackUnread,
    communityAdminModeration, communityResolveModeration,
    feedbackAdminInsights, feedbackGenerateInsights,
    thesisModels, thesisSetModel,
    adminMarketstackStatus, adminMarketstackSettings, adminMarketstackRefreshDirectory,
    adminLogos, adminLogoSave, adminLogoReset,
} from "@/lib/api";
import { CompanyLogo } from "@/components/CompanyIdentity";

const TYPE_META = {
    bug: { label: "Error", icon: Bug, color: "#B32A22" },
    suggestion: { label: "Idea", icon: Lightbulb, color: "#C99700" },
    feature: { label: "Mejora", icon: Wand2, color: "#052049" },
};
const STATUSES = [["new", "Nuevo"], ["in_progress", "En curso"], ["resolved", "Resuelto"]];
const STATUS_LABEL = Object.fromEntries(STATUSES);

function fmtDate(iso) {
    try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
}

export default function AdminFeedback() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user) { setIsAdmin(false); return; }
        feedbackUnread().then((r) => setIsAdmin(!!r.is_admin)).catch(() => setIsAdmin(false));
    }, [user, loading]);

    if (loading || isAdmin === null) return <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
    return <AdminInner isAdmin={isAdmin} navigate={navigate} />;
}

function AdminInner({ isAdmin, navigate }) {
    const [tab, setTab] = useState("feedback");
    if (!isAdmin) return (
        <div className="py-20 text-center" data-testid="admin-feedback-denied">
            <ShieldCheck className="mx-auto mb-3 opacity-30" size={40} />
            <div className="font-serif text-2xl mb-2">Acceso restringido</div>
            <p className="text-sm text-[#4A4A4A]">Esta página es solo para el administrador.</p>
            <button onClick={() => navigate("/")} className="btn-primary mt-4 !px-4 !py-2">Volver al inicio</button>
        </div>
    );

    return (
        <div data-testid="admin-feedback-page">
            <div className="flex items-center gap-3 mb-6">
                <ShieldCheck size={22} />
                <div>
                    <h1 className="font-serif text-3xl leading-none">Comunidad · Administración</h1>
                    <div className="overline text-[#4A4A4A] mt-1">Sugerencias, encuestas y moderación</div>
                </div>
            </div>
            <div className="flex flex-wrap border border-black mb-5 max-w-2xl">
                {[["feedback", "Sugerencias y encuestas"], ["moderation", "Moderación"], ["insights", "Insights IA"], ["cost", "Coste IA"], ["marketstack", "Marketstack"], ["logos", "Logos"]].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`flex-1 min-w-[45%] sm:min-w-0 py-2.5 text-xs uppercase tracking-[0.12em] font-semibold ${tab === k ? "bg-black text-[#FDF1E6]" : "hover:bg-black/5"}`}
                        data-testid={`admin-tab-${k}`}>{l}</button>
                ))}
            </div>
            {tab === "feedback" ? (<><SurveysAdmin /><ThreadsAdmin /></>)
                : tab === "moderation" ? <ModerationQueue />
                    : tab === "insights" ? <InsightsAdmin />
                        : tab === "marketstack" ? <MarketstackAdmin />
                            : tab === "logos" ? <LogosAdmin />
                                : <CostAdmin />}
        </div>
    );
}

function InsightsAdmin() {
    const [ins, setIns] = useState(undefined); // undefined=loading, null=none
    const [gen, setGen] = useState(false);

    const load = useCallback(async () => {
        try { const r = await feedbackAdminInsights(); setIns(r.insights || null); }
        catch { setIns(null); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const generate = async () => {
        setGen(true);
        try { const r = await feedbackGenerateInsights(); setIns(r.insights || null); toast.success("Insights de negocio actualizados"); }
        catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el análisis"); }
        finally { setGen(false); }
    };

    const PRIO = { P0: "#B32A22", P1: "#C99700", P2: "#052049" };
    const Section = ({ title, icon: Icon, items, color, testid }) => (
        <div className="border border-black/15 bg-white p-3" data-testid={testid}>
            <div className="flex items-center gap-2 mb-2" style={{ color }}><Icon size={16} /><span className="overline">{title}</span></div>
            {(!items || !items.length) ? <div className="text-xs text-[#4A4A4A] italic">Sin señales relevantes.</div>
                : <ul className="space-y-2">
                    {items.map((it, i) => (
                        <li key={i} className="text-sm">
                            <div className="flex items-center gap-2">
                                <span className="font-medium">{it.title}</span>
                                {it.mentions != null && <span className="text-[10px] text-[#4A4A4A] border border-black/20 px-1.5 py-0.5">{it.mentions} menc.</span>}
                            </div>
                            {it.detail && <div className="text-xs text-[#4A4A4A] mt-0.5">{it.detail}</div>}
                        </li>
                    ))}
                </ul>}
        </div>
    );

    if (ins === undefined) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
    return (
        <div data-testid="admin-insights">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="font-serif text-xl leading-none">Insights de negocio</div>
                    <div className="overline text-[#4A4A4A] mt-1">IA sobre sugerencias + actividad de la comunidad</div>
                </div>
                <button onClick={generate} disabled={gen}
                    className="btn-primary !px-3 !py-1.5 flex items-center gap-1.5 text-xs disabled:opacity-50" data-testid="insights-generate-btn">
                    {gen ? <Loader2 size={14} className="animate-spin" /> : (ins ? <RefreshCw size={14} /> : <Sparkles size={14} />)}
                    {ins ? "Actualizar" : "Generar insights"}
                </button>
            </div>
            {!ins ? (
                <div className="border border-black/15 bg-white p-8 text-center" data-testid="insights-empty">
                    <Sparkles className="mx-auto mb-2 opacity-30" size={32} />
                    <p className="text-sm text-[#4A4A4A]">Aún no has generado insights. Pulsa «Generar insights» para que la IA resuma el feedback y la comunidad.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="border border-black bg-[#FDF1E6]/60 p-3">
                        <div className="flex items-center justify-between mb-1">
                            <span className="overline flex items-center gap-1.5 text-[#052049]"><Sparkles size={13} /> Pulso del producto</span>
                            <span className="text-[10px] text-[#4A4A4A]" data-testid="insights-updated">Actualizado {fmtDate(ins.generated_at)} · {ins.sources?.feedback || 0} sugerencias · {ins.sources?.community || 0} de comunidad</span>
                        </div>
                        <p className="text-sm text-[#2A2A2A]" data-testid="insights-summary">{ins.summary}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Section title="Fricciones repetidas" icon={AlertTriangle} items={ins.frictions} color="#B32A22" testid="insights-frictions" />
                        <Section title="Más pedidas" icon={Lightbulb} items={ins.top_requests} color="#C99700" testid="insights-requests" />
                        <Section title="Bugs frecuentes" icon={Bug} items={ins.frequent_bugs} color="#052049" testid="insights-bugs" />
                    </div>
                    <div className="border border-black/15 bg-white p-3" data-testid="insights-roadmap">
                        <div className="flex items-center gap-2 mb-2 text-[#1D7044]"><Rocket size={16} /><span className="overline">Mini-roadmap priorizado</span></div>
                        {(!ins.roadmap || !ins.roadmap.length) ? <div className="text-xs text-[#4A4A4A] italic">Sin propuestas.</div>
                            : <ol className="space-y-2">
                                {ins.roadmap.map((r, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm" data-testid={`roadmap-item-${i}`}>
                                        <span className="text-[10px] font-bold text-white px-1.5 py-0.5 mt-0.5 shrink-0" style={{ background: PRIO[r.priority] || "#4A4A4A" }}>{r.priority}</span>
                                        <div><span className="font-medium">{r.title}</span>{r.why && <div className="text-xs text-[#4A4A4A] mt-0.5">{r.why}</div>}</div>
                                    </li>
                                ))}
                            </ol>}
                    </div>
                </div>
            )}
        </div>
    );
}

const fmtEur = (n) => `${(Number(n) || 0).toFixed(n >= 1 ? 2 : 4)} €`;

function CostAdmin() {
    const [data, setData] = useState(undefined); // undefined=loading, null=error
    const [saving, setSaving] = useState(null);

    const load = useCallback(async () => {
        try { setData(await thesisModels()); }
        catch { setData(null); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const choose = async (key) => {
        if (!data || key === data.active || saving) return;
        setSaving(key);
        try {
            const res = await thesisSetModel(key);
            setData((d) => ({ ...d, active: res.active }));
            const p = data.presets.find((x) => x.key === key);
            toast.success(`Modelo activo: ${p?.label || key}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo cambiar el modelo");
        } finally {
            setSaving(null);
        }
    };

    if (data === undefined) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
    if (!data) return <div className="border border-black/15 bg-white p-8 text-center text-sm text-[#4A4A4A]">No se pudo cargar el coste de IA.</div>;

    const total = data.presets.reduce((s, p) => s + (Number(p.cost_eur) || 0), 0);

    return (
        <div data-testid="admin-cost">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="font-serif text-xl leading-none">Coste IA · tesis</div>
                    <div className="overline text-[#4A4A4A] mt-1">Preset activo, uso y € estimados por preset</div>
                </div>
                <span className="text-xs text-[#4A4A4A] flex items-center gap-1.5" data-testid="cost-total">
                    <Euro size={13} /> Total estimado: <span className="font-semibold text-[#052049]">{fmtEur(total)}</span>
                </span>
            </div>
            <div className="border border-[#B8860B] bg-[#FCF6E8] p-3 mb-4 flex items-start gap-2" data-testid="cost-preset-warning">
                <AlertTriangle size={14} className="text-[#B8860B] shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#7a5a10] leading-snug">
                    <span className="font-semibold">Cuidado:</span> staging y producción comparten la misma base de datos, así que este preset es un ajuste global — no exclusivo de staging. Si lo cambias para probar, producción lo heredará en su próximo reinicio/deploy si no lo dejas como estaba antes. Antes de mergear a <code>main</code>, comprueba que el preset activo es el que quieres para producción (actualmente <code>equilibrado_plus</code>).
                </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="cost-presets">
                {data.presets.map((p) => {
                    const active = p.key === data.active;
                    return (
                        <div key={p.key} className={`border p-3 flex flex-col ${active ? "border-black bg-[#FDF1E6]/60" : "border-black/15 bg-white"}`} data-testid={`cost-preset-${p.key}`}>
                            <button
                                onClick={() => choose(p.key)}
                                disabled={!!saving}
                                title={`${p.desc}\n\nInvestigación: ${p.investigator}\nSíntesis/scoring: ${p.synthesizer}`}
                                className={`w-full px-2 py-2 text-xs uppercase tracking-[0.1em] font-semibold border flex items-center justify-center gap-1.5 transition-colors ${
                                    active ? "bg-black text-[#FDF1E6] border-black" : "bg-white text-[#4A4A4A] border-black/30 hover:bg-[#F5E4D4]"
                                } disabled:opacity-60`}
                                data-testid={`cost-preset-btn-${p.key}`}
                            >
                                {saving === p.key ? <Loader2 size={13} className="animate-spin" /> : active ? <Check size={13} /> : <Cpu size={13} />}
                                {p.label}
                            </button>
                            <div className="mt-2 text-[11px] text-[#4A4A4A] leading-snug">{p.investigator} · {p.synthesizer}</div>
                            <div className="mt-auto pt-2 flex items-center justify-between text-[11px] font-mono">
                                <span title="Generaciones realizadas con este preset">{p.theses} tesis</span>
                                <span className="font-semibold text-[#052049]" title="Coste estimado acumulado (€)">{fmtEur(p.cost_eur)}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
            <p className="text-[11px] text-[#7A7A7A] mt-3">Coste estimado a partir del nº de tokens por llamada (no es la factura real de cada proveedor). Pulsa un preset para activarlo.</p>
        </div>
    );
}

const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));
const MS_KIND_LABEL = { precios: "Precios (cierres e histórico)", perfiles: "Perfiles de empresa", buscador: "Búsqueda en vivo", directorio: "Directorio del buscador" };

// ---- Company logos: upload by hand the ones the automatic search gets wrong or too small ----
function LogosAdmin() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("attention"); // attention | all
    const [newTicker, setNewTicker] = useState("");
    const [busy, setBusy] = useState(null);
    const load = useCallback(async () => {
        try { setData(await adminLogos()); } catch { setData({ logos: [], error: true }); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (ticker, body, ok) => {
        setBusy(ticker);
        try { await adminLogoSave(ticker, body); toast.success(ok); await load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
        finally { setBusy(null); }
    };
    const reset = async (ticker) => {
        setBusy(ticker);
        try { await adminLogoReset(ticker); toast.success(`${ticker}: vuelve al logo automático (se buscará en la próxima visita)`); await load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "No se pudo restablecer"); }
        finally { setBusy(null); }
    };

    if (!data) return <div className="flex items-center gap-2 text-sm text-[#4A4A4A] py-8 justify-center"><Loader2 className="animate-spin" size={16} /> Cargando…</div>;
    const rows = (data.logos || []).filter((r) => filter === "all" || r.needs_attention || r.manual);
    const attention = (data.logos || []).filter((r) => r.needs_attention).length;
    return (
        <div data-testid="admin-logos">
            <div className="border border-black bg-white p-4 mb-4">
                <div className="font-serif text-xl leading-none mb-2">Logos de las empresas</div>
                <p className="text-xs text-[#4A4A4A] leading-relaxed max-w-3xl">
                    El logo se busca solo en la web de cada empresa y se comparte con todos los usuarios. Si sale pequeño (menos de {data.good_size} px), pixelado o
                    no sale, súbelo aquí: sustituye al automático para todos y no se vuelve a buscar hasta que pulses «Volver a automático».
                    PNG, SVG, WEBP, JPG o ICO, hasta 512 KB, mejor cuadrado y de 128 px o más (el icono de la empresa, no el logotipo apaisado).
                    Conviene sacarlo de su página de prensa o «brand assets». El color es el del ticker en la ficha: si lo dejas vacío, se calcula del logo.
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                    {[["attention", `Por revisar (${attention})`], ["all", `Todos (${(data.logos || []).length})`]].map(([k, l]) => (
                        <button key={k} onClick={() => setFilter(k)} className={`px-2.5 py-1 text-xs border ${filter === k ? "bg-black text-[#FDF1E6] border-black" : "bg-white border-black/30 hover:border-black"}`} data-testid={`admin-logos-filter-${k}`}>{l}</button>
                    ))}
                    <span className="flex-1" />
                    <input value={newTicker} onChange={(e) => setNewTicker(e.target.value.toUpperCase())} placeholder="Otro ticker (p. ej. SAN.MC)"
                        className="border border-black/30 bg-white px-2 py-1 text-xs w-44 outline-none focus:border-black" data-testid="admin-logos-new-ticker" />
                    <label className={`btn-ghost !py-1 !px-2.5 text-xs inline-flex items-center gap-1.5 cursor-pointer ${!newTicker.trim() ? "opacity-40 pointer-events-none" : ""}`}>
                        <Plus size={13} /> Subir logo
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/x-icon,.ico" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f && newTicker.trim()) save(newTicker.trim(), { file: f }, `${newTicker.trim()}: logo subido`).then(() => setNewTicker("")); }} />
                    </label>
                </div>
            </div>
            {data.error && <div className="text-sm text-[#B32A22] mb-3">No se pudieron cargar los logos.</div>}
            {rows.length === 0 && !data.error && <div className="border border-dashed border-black/30 p-6 text-center text-sm text-[#4A4A4A]">{filter === "attention" ? "Ningún logo pendiente de revisar." : "Aún no hay logos guardados: se guardan al abrir la ficha o el KPI de cada empresa."}</div>}
            <div className="flex flex-col gap-2">
                {rows.map((r) => <LogoRow key={r.ticker} r={r} busy={busy === r.ticker} onSave={save} onReset={reset} />)}
            </div>
        </div>
    );
}

function LogoRow({ r, busy, onSave, onReset }) {
    const [color, setColor] = useState(r.brand_color || "#052049");
    useEffect(() => { setColor(r.brand_color || "#052049"); }, [r.brand_color]);
    const small = r.has_logo && r.width && r.needs_attention;
    return (
        <div className="border border-black/20 bg-white p-3 flex flex-wrap items-center gap-3" data-testid={`admin-logo-${r.ticker}`}>
            <CompanyLogo logoUrl={r.logo_url} brandColor={r.brand_color} ticker={r.ticker} className="w-12 h-12" initialsClass="text-base" testid={`admin-logo-img-${r.ticker}`} />
            <div className="min-w-0 flex-1 basis-40">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold">{r.ticker}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${r.manual ? "border-[#052049] text-[#052049]" : "border-black/20 text-[#7A7A7A]"}`}>{r.manual ? "Manual" : "Automático"}</span>
                    {!r.has_logo && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[#B32A22] text-[#B32A22]">Sin logo</span>}
                    {small && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[#B8860B] text-[#7a5a10]">{r.width} px · pequeño</span>}
                    {r.has_logo && !small && <span className="text-[10px] text-[#7A7A7A]">{r.width ? `${r.width} px` : (r.content_type === "image/svg+xml" ? "SVG" : "")}</span>}
                </div>
                <div className="text-[11px] text-[#7A7A7A] truncate">
                    {r.website ? <a href={r.website} target="_blank" rel="noreferrer" className="hover:underline">{r.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}</a> : "Sin web"}
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-1.5 text-xs" title="Color del ticker en la ficha">
                    <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-7 h-7 p-0 border border-black/30 cursor-pointer" data-testid={`admin-logo-color-${r.ticker}`} />
                    <button onClick={() => onSave(r.ticker, { brandColor: color }, `${r.ticker}: color guardado`)} disabled={busy || color === r.brand_color} className="btn-ghost !py-1 !px-2 text-xs disabled:opacity-40">Guardar color</button>
                    {r.brand_color && <button onClick={() => onSave(r.ticker, { brandColor: "auto" }, `${r.ticker}: color calculado del logo`)} disabled={busy} className="text-[11px] text-[#7A7A7A] hover:underline disabled:opacity-40">Auto</button>}
                </label>
                <label className={`btn-primary !py-1 !px-2.5 text-xs inline-flex items-center gap-1.5 cursor-pointer ${busy ? "opacity-40 pointer-events-none" : ""}`} data-testid={`admin-logo-upload-${r.ticker}`}>
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Subir logo
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/x-icon,.ico" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onSave(r.ticker, { file: f }, `${r.ticker}: logo subido`); }} />
                </label>
                {r.manual && <button onClick={() => onReset(r.ticker)} disabled={busy} className="btn-ghost !py-1 !px-2.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40" data-testid={`admin-logo-reset-${r.ticker}`}><RefreshCw size={13} /> Volver a automático</button>}
            </div>
        </div>
    );
}

function MarketstackAdmin() {
    const [data, setData] = useState(undefined); // undefined=loading, null=error
    const [busy, setBusy] = useState(null);
    const [reserve, setReserve] = useState("");

    const load = useCallback(async () => {
        try {
            const d = await adminMarketstackStatus();
            setData(d);
            setReserve(String(d?.settings?.reserve ?? ""));
        } catch { setData(null); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (body, key, okMsg) => {
        setBusy(key);
        try {
            const settings = await adminMarketstackSettings(body);
            setData((d) => ({ ...d, settings }));
            toast.success(okMsg);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar el ajuste");
        } finally { setBusy(null); }
    };

    const rebuild = async () => {
        if (!window.confirm("Reconstruir el directorio del buscador gasta unas 200 peticiones de la cuota mensual. ¿Continuar?")) return;
        setBusy("dir");
        try {
            await adminMarketstackRefreshDirectory();
            toast.success("Reconstrucción iniciada (tarda unos minutos). Pulsa «Actualizar» para ver el resultado.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo iniciar la reconstrucción");
        } finally { setBusy(null); }
    };

    if (data === undefined) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
    if (!data) return <div className="border border-black/15 bg-white p-8 text-center text-sm text-[#4A4A4A]">No se pudo cargar el estado de Marketstack.</div>;

    const q = data.quota || {};
    const st = data.settings || {};
    const usage = data.usage || {};
    const days = usage.days || [];
    const usedPct = q.limit && q.remaining != null ? Math.min(100, Math.max(0, ((q.limit - q.remaining) / q.limit) * 100)) : null;
    const low = q.remaining != null && q.remaining < (st.reserve ?? 0);
    const last14 = days.slice(0, 14);
    const maxDay = Math.max(1, ...last14.map((d) => d.requests || 0));
    const byKind = {};
    days.slice(0, 30).forEach((d) => Object.entries(d.by_kind || {}).forEach(([k, v]) => { byKind[k] = (byKind[k] || 0) + v; }));
    const dir = data.directory;
    const ps = data.price_store || {};

    const Stat = ({ label, value, sub, testid, warn }) => (
        <div className={`border p-3 bg-white ${warn ? "border-[#B32A22]" : "border-black/15"}`} data-testid={testid}>
            <div className="overline text-[#4A4A4A]">{label}</div>
            <div className={`font-mono text-2xl mt-1 ${warn ? "text-[#B32A22]" : ""}`}>{value}</div>
            {sub && <div className="text-[11px] text-[#4A4A4A] mt-1 leading-snug">{sub}</div>}
        </div>
    );

    return (
        <div data-testid="admin-marketstack">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="font-serif text-xl leading-none">Marketstack · precios</div>
                    <div className="overline text-[#4A4A4A] mt-1">Cuota, consumo y caché de precios</div>
                </div>
                <button onClick={load} className="btn-ghost !px-3 !py-1.5 text-xs flex items-center gap-1.5" data-testid="ms-reload">
                    <RefreshCw size={13} /> Actualizar
                </button>
            </div>

            {!data.configured && (
                <div className="border border-[#B32A22] bg-[#FBEDEC] p-3 mb-4 text-[12px] text-[#B32A22]" data-testid="ms-not-configured">
                    Falta <code>MARKETSTACK_API_KEY</code> en este entorno: no se piden precios nuevos, solo se sirve la caché.
                </div>
            )}
            <div className="border border-[#B8860B] bg-[#FCF6E8] p-3 mb-4 flex items-start gap-2">
                <AlertTriangle size={14} className="text-[#B8860B] shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#7a5a10] leading-snug">
                    Staging y producción comparten base de datos y clave de Marketstack: la cuota, el consumo y estos ajustes son <span className="font-semibold">globales</span>. La cuota restante es la última que devolvió Marketstack (se actualiza con cada petición).
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <Stat testid="ms-remaining" label="Cuota restante" warn={low}
                    value={fmtInt(q.remaining)}
                    sub={q.limit ? `de ${fmtInt(q.limit)} este mes${usedPct != null ? ` · ${usedPct.toFixed(1)} % usado` : ""}${q.updated_at ? ` · lectura ${fmtDate(q.updated_at)}` : ""}` : "Aún sin lectura (llega con la primera petición)"} />
                <Stat testid="ms-today" label="Peticiones hoy" value={fmtInt(usage.today)} sub="Día UTC" />
                <Stat testid="ms-30d" label="Últimos 30 días" value={fmtInt(usage.last_30_days)} sub="Contadas por la app" />
                <Stat testid="ms-cache" label="Tickers en caché" value={fmtInt(ps.tickers)}
                    sub={`${fmtInt(ps.unsupported)} sin cobertura (cripto, índices…)`} />
            </div>
            {usedPct != null && (
                <div className="h-2 w-full bg-black/10 mb-5" title={`${usedPct.toFixed(1)} % de la cuota mensual usada`}>
                    <div className="h-2" style={{ width: `${usedPct}%`, background: low ? "#B32A22" : "var(--chart-blue)" }} />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                <div className="border border-black/15 bg-white p-3" data-testid="ms-days">
                    <div className="overline text-[#4A4A4A] mb-2">Peticiones por día (últimos 14)</div>
                    {!last14.length ? <div className="text-xs text-[#4A4A4A] italic">Sin peticiones registradas todavía.</div> : (
                        <div className="space-y-1">
                            {last14.map((d) => (
                                <div key={d.date} className="flex items-center gap-2 text-[11px] font-mono"
                                    title={Object.entries(d.by_kind || {}).map(([k, v]) => `${MS_KIND_LABEL[k] || k}: ${v}`).join("\n")}>
                                    <span className="w-[4.5rem] shrink-0 text-[#4A4A4A]">{d.date.slice(5)}</span>
                                    <div className="flex-1 min-w-0 h-2.5 bg-black/5">
                                        <div className="h-2.5" style={{ width: `${((d.requests || 0) / maxDay) * 100}%`, background: "var(--chart-blue)" }} />
                                    </div>
                                    <span className="w-12 shrink-0 text-right">{fmtInt(d.requests)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="border border-black/15 bg-white p-3" data-testid="ms-kinds">
                    <div className="overline text-[#4A4A4A] mb-2">En qué se gasta (30 días)</div>
                    {!Object.keys(byKind).length ? <div className="text-xs text-[#4A4A4A] italic">Sin datos.</div> : (
                        <ul className="space-y-1.5 text-sm">
                            {Object.entries(byKind).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                                <li key={k} className="flex items-center justify-between gap-3">
                                    <span>{MS_KIND_LABEL[k] || k}</span><span className="font-mono">{fmtInt(v)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="text-[11px] text-[#7A7A7A] mt-3 leading-snug">
                        Cada ticker nuevo cuesta ~3 peticiones (10 años de histórico); después, como mucho 1 al día por ticker consultado. Un dividendo o split recarga ese ticker entero.
                    </p>
                </div>
            </div>

            <div className="border border-black/15 bg-white p-3 mb-3" data-testid="ms-controls">
                <div className="overline text-[#4A4A4A] mb-3">Control de consumo</div>
                <div className="flex items-start gap-3 flex-wrap mb-4">
                    <span className={`text-[11px] font-semibold uppercase tracking-[0.1em] px-2 py-1 border ${st.paused ? "border-[#B32A22] text-[#B32A22]" : "border-[#1D7044] text-[#1D7044]"}`} data-testid="ms-state">
                        {st.paused ? "En pausa" : "Activo"}
                    </span>
                    <button onClick={() => save({ paused: !st.paused }, "pause", st.paused ? "Consumo reanudado" : "Consumo en pausa")}
                        disabled={!!busy} className="btn-primary !px-3 !py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-60" data-testid="ms-toggle-pause">
                        {busy === "pause" ? <Loader2 size={13} className="animate-spin" /> : st.paused ? <Play size={13} /> : <Pause size={13} />}
                        {st.paused ? "Reanudar consumo" : "Pausar consumo"}
                    </button>
                    <p className="text-[11px] text-[#4A4A4A] leading-snug basis-full sm:basis-0 sm:flex-1 min-w-0">
                        En pausa no se hace ninguna petición a Marketstack: se sirven los precios ya guardados. Un ticker que nunca se haya abierto usará el precio de Yahoo como respaldo hasta reanudar.
                    </p>
                </div>
                <div className="flex items-end gap-2 flex-wrap mb-4">
                    <label className="flex flex-col gap-1 text-[11px] text-[#4A4A4A]">
                        <span>Reserva (peticiones)</span>
                        <input type="number" min="0" max="100000" value={reserve} onChange={(e) => setReserve(e.target.value)}
                            className="input-paper font-mono w-full max-w-[9rem]" data-testid="ms-reserve" />
                    </label>
                    <button onClick={() => save({ reserve: Number(reserve) }, "reserve", "Reserva guardada")}
                        disabled={!!busy || reserve === "" || Number(reserve) === st.reserve}
                        className="btn-ghost !px-3 !py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50" data-testid="ms-save-reserve">
                        {busy === "reserve" ? <Loader2 size={13} className="animate-spin" /> : <Gauge size={13} />} Guardar
                    </button>
                    <p className="text-[11px] text-[#4A4A4A] leading-snug basis-full">
                        Por debajo de esta cuota restante se detienen las tareas masivas (reconstruir el directorio del buscador) para dejar la cuota a los precios. Por defecto {fmtInt(data.default_reserve)}.
                    </p>
                </div>
                <div className="flex items-start gap-3 flex-wrap">
                    <div className="text-[12px] leading-snug min-w-0 flex-1 basis-full sm:basis-0">
                        <div className="font-medium">Directorio del buscador</div>
                        <div className="text-[#4A4A4A]">
                            {dir?.refreshed_at
                                ? <>{fmtInt(dir.total)} valores · última reconstrucción {fmtDate(dir.refreshed_at)}{dir.failed?.length ? <> · <span className="text-[#B32A22]">fallaron: {dir.failed.join(", ")}</span></> : null}</>
                                : "Aún no construido (se construye solo al arrancar y el día 1 de cada mes)."}
                        </div>
                    </div>
                    <button onClick={rebuild} disabled={!!busy || st.paused}
                        className="btn-ghost !px-3 !py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50" data-testid="ms-rebuild-directory">
                        {busy === "dir" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Reconstruir (~200 peticiones)
                    </button>
                </div>
            </div>
        </div>
    );
}

function ModerationQueue() {
    const [items, setItems] = useState(null);
    const [status, setStatus] = useState("open");

    const load = useCallback(async () => {
        try { const r = await communityAdminModeration(status); setItems(r.items || []); }
        catch { setItems([]); }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const act = async (id, action) => {
        try { await communityResolveModeration(id, action); toast.success(action === "delete" ? "Contenido borrado" : "Aprobado (visible)"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    return (
        <div className="border border-black bg-white" data-testid="admin-moderation">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-black">
                <div className="flex items-center gap-2"><Flag size={18} /><span className="font-semibold text-sm uppercase tracking-wide">Cola de moderación</span></div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-black px-2 py-1 text-sm bg-white" data-testid="mod-status-filter">
                    <option value="open">Abiertos</option>
                    <option value="resolved">Resueltos</option>
                    <option value="all">Todos</option>
                </select>
            </div>
            {items === null ? <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>
                : !items.length ? <div className="p-8 text-center text-sm text-[#4A4A4A]" data-testid="mod-empty">Nada pendiente de moderar. 🎉</div>
                : <div className="divide-y divide-black/10">
                    {items.map((m) => (
                        <div key={m.id} className="p-3" data-testid={`mod-item-${m.id}`}>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {m.source === "ai"
                                    ? <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 border border-[#052049] text-[#052049]"><Bot size={12} /> IA</span>
                                    : <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 border border-[#B32A22] text-[#B32A22]"><Flag size={12} /> {m.reporters} reporte{m.reporters !== 1 ? "s" : ""}</span>}
                                <span className="overline text-[#4A4A4A]">{m.target_type === "topic" ? "Tema/Idea" : "Respuesta"}{m.ticker ? ` · ${m.ticker}` : ""}</span>
                                {m.hidden && <span className="overline text-[#B32A22]">oculto</span>}
                                {m.removed && <span className="overline text-[#4A4A4A]">borrado</span>}
                            </div>
                            <div className="text-sm font-medium">{m.preview}</div>
                            {m.content_body && m.content_body !== m.preview && <div className="text-xs text-[#4A4A4A] mt-0.5 line-clamp-2">{m.content_body}</div>}
                            <div className="text-xs text-[#B32A22] mt-1">Motivo: {m.reason}</div>
                            <div className="text-[11px] text-[#4A4A4A] mt-0.5">{m.author_name} · {fmtDate(m.created_at)}</div>
                            {m.status === "open" && (
                                <div className="flex gap-2 mt-2">
                                    <button onClick={() => act(m.id, "approve")} className="flex items-center gap-1 text-xs border border-[#1D7044] text-[#1D7044] px-2 py-1 hover:bg-[#1D7044] hover:text-white transition-colors" data-testid={`mod-approve-${m.id}`}><Check size={13} /> Aprobar</button>
                                    <button onClick={() => act(m.id, "delete")} className="flex items-center gap-1 text-xs border border-[#B32A22] text-[#B32A22] px-2 py-1 hover:bg-[#B32A22] hover:text-white transition-colors" data-testid={`mod-delete-${m.id}`}><Trash2 size={13} /> Borrar</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>}
        </div>
    );
}

function ThreadsAdmin() {
    const [threads, setThreads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState(null);
    const [type, setType] = useState(null);
    const [selected, setSelected] = useState(null);
    const timer = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await feedbackAdminThreads({ q: q || null, status, type }); setThreads(r.threads || []); }
        catch { setThreads([]); }
        finally { setLoading(false); }
    }, [q, status, type]);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(load, 250);
        return () => clearTimeout(timer.current);
    }, [load]);

    return (
        <div className="border border-black bg-white" data-testid="admin-threads">
            <div className="p-3 border-b border-black flex flex-wrap items-center gap-2">
                <div className="flex items-center border border-black flex-1 min-w-[200px]">
                    <Search size={16} className="ml-2 text-[#4A4A4A]" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por asunto, email o nombre…"
                        className="flex-1 px-2 py-1.5 outline-none text-sm font-mono" data-testid="admin-search-input" />
                </div>
                <select value={type || ""} onChange={(e) => setType(e.target.value || null)} className="border border-black px-2 py-1.5 text-sm bg-white" data-testid="admin-filter-type">
                    <option value="">Todos los tipos</option>
                    <option value="bug">Errores</option>
                    <option value="suggestion">Ideas</option>
                    <option value="feature">Mejoras</option>
                </select>
                <select value={status || ""} onChange={(e) => setStatus(e.target.value || null)} className="border border-black px-2 py-1.5 text-sm bg-white" data-testid="admin-filter-status">
                    <option value="">Todos los estados</option>
                    {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
            </div>

            <div className="grid md:grid-cols-2 min-h-[400px]">
                <div className="border-r border-black/10 max-h-[600px] overflow-y-auto">
                    {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>
                        : !threads.length ? <div className="p-8 text-center text-sm text-[#4A4A4A]" data-testid="admin-threads-empty">Sin resultados.</div>
                        : threads.map((t) => {
                            const m = TYPE_META[t.type] || TYPE_META.bug;
                            return (
                                <button key={t.id} onClick={() => setSelected(t.id)}
                                    className={`w-full text-left px-3 py-2.5 border-b border-black/10 flex items-start gap-2 transition-colors ${selected === t.id ? "bg-[#F5E4D4]" : "hover:bg-black/5"}`}
                                    data-testid={`admin-thread-${t.id}`}>
                                    {React.createElement(m.icon, { size: 15, style: { color: m.color, marginTop: 2 } })}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{t.subject}</div>
                                        <div className="overline text-[#4A4A4A] mt-0.5 truncate">{t.user_email} · {STATUS_LABEL[t.status]} · {fmtDate(t.last_message_at)}</div>
                                    </div>
                                    {t.unread_for_admin > 0 && <span className="bg-[#B32A22] text-white text-[10px] font-bold px-1.5 rounded-full shrink-0">{t.unread_for_admin}</span>}
                                </button>
                            );
                        })}
                </div>
                <div className="max-h-[600px] overflow-y-auto">
                    {selected
                        ? <AdminThreadView id={selected} onChanged={load} />
                        : <div className="p-8 text-center text-sm text-[#4A4A4A] h-full flex items-center justify-center">Selecciona un mensaje para verlo.</div>}
                </div>
            </div>
        </div>
    );
}

function AdminThreadView({ id, onChanged }) {
    const [data, setData] = useState(null);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [shotUrl, setShotUrl] = useState(null);

    const load = useCallback(async () => {
        const r = await feedbackGetThread(id);
        setData(r); onChanged && onChanged();
        if (r.thread.has_screenshot) {
            try { const b = await feedbackScreenshot(id); setShotUrl(URL.createObjectURL(b)); } catch { /* ignore */ }
        } else setShotUrl(null);
    }, [id]); // eslint-disable-line

    useEffect(() => { setData(null); setShotUrl(null); load(); }, [load]);

    const send = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try { await feedbackReply(id, reply.trim()); setReply(""); await load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
        finally { setSending(false); }
    };

    const setStatus = async (s) => {
        try { await feedbackAdminUpdateStatus(id, s); await load(); toast.success("Estado actualizado"); }
        catch { toast.error("No se pudo actualizar"); }
    };

    if (!data) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
    const { thread, messages } = data;
    const meta = thread.metadata || {};

    return (
        <div className="flex flex-col h-full" data-testid="admin-thread-view">
            <div className="px-3 py-2 border-b border-black/10 bg-white/60">
                <div className="flex items-center gap-2 mb-2">
                    {STATUSES.map(([k, l]) => (
                        <button key={k} onClick={() => setStatus(k)}
                            className={`text-xs px-2 py-1 border transition-colors ${thread.status === k ? "bg-black text-white border-black" : "border-black/30 hover:border-black"}`}
                            data-testid={`admin-status-${k}`}>{l}</button>
                    ))}
                </div>
                <div className="text-[11px] text-[#4A4A4A] font-mono leading-relaxed" data-testid="admin-thread-meta">
                    <span className="text-black">{thread.user_name || thread.user_email}</span> · {meta.browser} {meta.os} · {meta.viewport} · {meta.language}<br />
                    Página: {meta.path} · Enviado: {fmtDate(thread.created_at)}
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] px-3 py-2 text-sm border border-black ${msg.sender === "admin" ? "bg-[#052049] text-white" : "bg-white"}`}>
                            <div className="whitespace-pre-wrap break-words">{msg.text}</div>
                            <div className={`text-[10px] mt-1 ${msg.sender === "admin" ? "text-white/60" : "text-[#4A4A4A]"}`}>{fmtDate(msg.created_at)}</div>
                        </div>
                    </div>
                ))}
                {shotUrl && (
                    <a href={shotUrl} target="_blank" rel="noreferrer" className="block border border-black" data-testid="admin-thread-screenshot">
                        <img src={shotUrl} alt="captura" className="w-full" />
                    </a>
                )}
            </div>
            <div className="p-2 border-t border-black flex gap-2 bg-white">
                <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Responder al usuario…" className="flex-1 border border-black px-3 py-2 text-sm outline-none" data-testid="admin-reply-input" />
                <button onClick={send} disabled={sending} className="btn-primary !px-3 disabled:opacity-50" data-testid="admin-reply-send">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
            </div>
        </div>
    );
}

function SurveysAdmin() {
    const [surveys, setSurveys] = useState([]);
    const [creating, setCreating] = useState(false);
    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState(["", ""]);

    const load = useCallback(() => feedbackSurveys().then((r) => setSurveys(r.surveys || [])).catch(() => {}), []);
    useEffect(() => { load(); }, [load]);

    const create = async () => {
        const opts = options.map((o) => o.trim()).filter(Boolean);
        if (!question.trim() || opts.length < 2) { toast.error("Escribe la pregunta y al menos 2 opciones"); return; }
        try {
            await feedbackCreateSurvey(question.trim(), opts);
            toast.success("Encuesta publicada para todos los usuarios");
            setQuestion(""); setOptions(["", ""]); setCreating(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    const toggle = async (s) => {
        try { await feedbackToggleSurvey(s.id, !s.active); load(); }
        catch { toast.error("No se pudo cambiar"); }
    };

    return (
        <div className="border border-black bg-white mb-6" data-testid="admin-surveys">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-black">
                <div className="flex items-center gap-2"><BarChart3 size={18} /><span className="font-semibold text-sm uppercase tracking-wide">Encuestas globales</span></div>
                <button onClick={() => setCreating((c) => !c)} className="btn-primary !px-3 !py-1.5 flex items-center gap-1.5 text-xs" data-testid="admin-survey-new-btn">
                    {creating ? <X size={14} /> : <Plus size={14} />}{creating ? "Cancelar" : "Nueva encuesta"}
                </button>
            </div>

            {creating && (
                <div className="p-3 border-b border-black/10 space-y-2 bg-[#FDF1E6]/50" data-testid="admin-survey-form">
                    <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Pregunta de la encuesta…"
                        className="w-full border border-black px-3 py-2 text-sm outline-none" data-testid="admin-survey-question" />
                    {options.map((o, i) => (
                        <div key={i} className="flex gap-2">
                            <input value={o} onChange={(e) => setOptions((a) => a.map((x, j) => j === i ? e.target.value : x))}
                                placeholder={`Opción ${i + 1}`} className="flex-1 border border-black px-3 py-1.5 text-sm outline-none" data-testid={`admin-survey-option-${i}`} />
                            {options.length > 2 && <button onClick={() => setOptions((a) => a.filter((_, j) => j !== i))} className="px-2 border border-black/30 hover:border-black"><X size={14} /></button>}
                        </div>
                    ))}
                    <div className="flex justify-between">
                        <button onClick={() => setOptions((a) => [...a, ""])} className="text-xs border border-black/30 px-2 py-1 hover:border-black" data-testid="admin-survey-add-option">+ Añadir opción</button>
                        <button onClick={create} className="btn-primary !px-4 !py-1.5 text-xs" data-testid="admin-survey-publish">Publicar</button>
                    </div>
                </div>
            )}

            <div className="divide-y divide-black/10">
                {!surveys.length ? <div className="p-4 text-center text-sm text-[#4A4A4A]">No hay encuestas.</div>
                    : surveys.map((s) => {
                        const total = s.total_votes || 0;
                        return (
                            <div key={s.id} className="p-3" data-testid={`admin-survey-${s.id}`}>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <div className="font-medium text-sm">{s.question}</div>
                                    <button onClick={() => toggle(s)} className={`text-xs px-2 py-1 border flex items-center gap-1 shrink-0 ${s.active ? "border-[#2E7D32] text-[#2E7D32]" : "border-black/30 text-[#4A4A4A]"}`} data-testid={`admin-survey-toggle-${s.id}`}>
                                        <Power size={12} />{s.active ? "Activa" : "Cerrada"}
                                    </button>
                                </div>
                                <div className="space-y-1">
                                    {s.options.map((opt, i) => {
                                        const count = s.results ? s.results[i] : 0;
                                        const pct = total ? Math.round((count / total) * 100) : 0;
                                        return (
                                            <div key={i} className="relative border border-black/20 overflow-hidden">
                                                <div className="absolute inset-y-0 left-0 bg-[#052049]/10" style={{ width: `${pct}%` }} />
                                                <div className="relative flex justify-between px-2 py-1 text-xs">
                                                    <span>{opt}</span><span className="font-mono text-[#4A4A4A]">{count} · {pct}%</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="overline text-[#4A4A4A] mt-1">{total} voto{total !== 1 ? "s" : ""}</div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}
